import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import {
  CheckCircle,
  Trophy,
  Printer,
  Table2,
  Unplug,
  Link2,
  ChevronLeft,
  ChevronRight,
  Search,
  AlertTriangle,
} from "lucide-react"
import { useEffect, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { RacesMap } from "@/lib/constants"
import { UserAvatar } from "./ui/user-avatar"
import { cn } from "@/lib/utils"
interface Player {
  id: number
  drop: number
  raceId: number
  points: number
  Person: {
    id: number
    avatar?: string
    fullName: string
    urlAvatar?: string
    phone?: string
    code: string
  }
}

interface Match {
  id: number
  table: number
  reported: number
  draw: boolean
  dropPlayer: boolean
  dropOpponent: boolean
  winnerVictories: number
  loserVictories: number
  Player?: Player
  Opponent?: Player
  playerId: number
  opponentId: number
  winnerId: number
  loserId: number

  modified?: Date
  ModifiedBy?: {
    id: number
    fullName: string
  }
}

interface MatchTableProps {
  matches: Match[]
  isAdmin?: boolean
  onReportResult: (matchId: number) => void
  showOnlyPending?: boolean
  onShowOnlyPendingChange?: (checked: boolean) => void
  canReport?: boolean
  canModifyReportedResults?: boolean
  onPrintMatches?: () => void
  canManageTables?: boolean
  canReleaseTables?: boolean
  isUpdatingTables?: boolean
  onReleaseTable?: (matchId: number) => Promise<void>
  onAssignPlayersToTable?: (input: {
    targetMatchId: number
    playerOneSourceMatchId: number
    playerOneId: number
    playerTwoSourceMatchId?: number
    playerTwoId?: number
  }) => Promise<void>
  playersWithoutTable?: { sourceMatchId: number; playerId: number; name: string; code: string }[]
  canSeeCode?: boolean
}

const getRaceName = (id?: number, isAdmin = false, mobile = false) => {
  if (!isAdmin) {
    return ""
  }
  const raza = RacesMap.get(id!) || "Sin raza"
  return `${mobile ? " - " : ""} Raza: ${raza}`
}

export const MatchTable = ({
  matches,
  onReportResult,
  showOnlyPending = false,
  onShowOnlyPendingChange,
  canReport = true,
  canModifyReportedResults = true,
  onPrintMatches,
  canManageTables = false,
  canReleaseTables = false,
  isUpdatingTables = false,
  onReleaseTable,
  onAssignPlayersToTable,
  playersWithoutTable = [],
  isAdmin = false,
  canSeeCode = true,
}: MatchTableProps) => {
  const [showReleaseConfirmDialog, setShowReleaseConfirmDialog] = useState(false)
  const [releaseTargetMatchId, setReleaseTargetMatchId] = useState<number | null>(null)

  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [assignTargetMatchId, setAssignTargetMatchId] = useState<number | null>(null)
  const [selectedPlayerOneId, setSelectedPlayerOneId] = useState("")
  const [selectedPlayerTwoId, setSelectedPlayerTwoId] = useState("")

  const [mobilePage, setMobilePage] = useState(0)
  const [tableSearch, setTableSearch] = useState("")

  const getPlayerInitials = (fullName: string) => {
    return fullName
      ?.split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const getMatchStatusBadge = (match: Match, showPending = false) => {
    if (match.reported) {
      if (match.draw) {
        return <Badge variant="default">Empate</Badge>
      } else if (match.winnerId) {
        return <Badge variant="info">Reportado</Badge>
      }
    } else if (showPending) {
      return <Badge variant="destructive">Falta reporte</Badge>
    }
    return <></>
  }

  const isByeMatch = (match: Match) => !match.Opponent

  const getScoreDisplay = (match: Match, player = 0) => {
    if (!match.reported && player === 0) {
      return <span>{getMatchStatusBadge(match, true)}</span>
    }

    if (isByeMatch(match)) {
      return (
        <div className="flex items-center justify-center gap-2 text-sm">
          {[0, 1].includes(player) && (
            <div className={`px-3 py-1 rounded-full font-bold text-lg bg-green-100 text-green-800`}>
              {match.winnerVictories}
            </div>
          )}
          {player === 0 && <span className="text-sm font-bold text-muted-foreground">Bye</span>}
          {[0, 2].includes(player) && (
            <div className={`px-3 py-1 rounded-full font-bold text-lg bg-red-100 text-red-800`}>
              {match.loserVictories}
            </div>
          )}
        </div>
      )
    }

    if (match.draw) {
      return (
        <div className="flex items-center justify-center gap-2">
          {[0, 1].includes(player) && (
            <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-bold text-lg">
              {match.winnerVictories}
            </div>
          )}
          {player === 0 && <span className="text-sm font-bold text-muted-foreground">VS</span>}
          {[0, 2].includes(player) && (
            <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-bold text-lg">
              {match.loserVictories}
            </div>
          )}
        </div>
      )
    }

    if (match.winnerId) {
      const isPlayerWinner = match.winnerId === match.playerId
      const playerScore = isPlayerWinner ? match.winnerVictories : match.loserVictories

      const opponentScore = isPlayerWinner ? match.loserVictories : match.winnerVictories

      return (
        <div className="flex items-center gap-2 justify-center">
          {[0, 1].includes(player) && (
            <div
              className={`px-3 py-1 rounded-full font-bold text-lg ${
                isPlayerWinner ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {playerScore}
            </div>
          )}
          {player === 0 && <span className="text-sm font-bold text-muted-foreground">VS</span>}
          {[0, 2].includes(player) && (
            <div
              className={`px-3 py-1 rounded-full font-bold text-lg ${
                !isPlayerWinner ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {opponentScore}
            </div>
          )}
        </div>
      )
    }

    return null
  }

  const filteredMatches = (
    showOnlyPending ? matches.filter((match) => !match.reported) : matches
  ).filter((match) => !tableSearch || String(match.table).includes(tableSearch))

  const MOBILE_PAGE_SIZE = 20
  const totalMobilePages = Math.max(1, Math.ceil(filteredMatches.length / MOBILE_PAGE_SIZE))
  const mobilePageStart = mobilePage * MOBILE_PAGE_SIZE
  const mobilePageEnd = mobilePageStart + MOBILE_PAGE_SIZE

  /**
   * Jugadores que aparecen más de una vez en los emparejamientos de la ronda
   * (en dos mesas, o como jugador y oponente de la misma mesa).
   */
  const duplicatedPlayers = (() => {
    const seen = new Map<number, { name: string; code?: string; tables: number[] }>()
    matches.forEach((match) => {
      ;[
        { id: match.playerId, player: match.Player },
        { id: match.opponentId, player: match.Opponent },
      ].forEach(({ id, player }) => {
        if (!id) return
        const entry = seen.get(id) ?? {
          name: player?.Person?.fullName || `Jugador #${id}`,
          code: player?.Person?.code,
          tables: [],
        }
        entry.tables.push(match.table)
        seen.set(id, entry)
      })
    })
    return Array.from(seen.entries())
      .filter(([, value]) => value.tables.length > 1)
      .map(([playerId, value]) => ({ playerId, ...value }))
  })()

  /**
   * Mesas incompletas: sin jugador o sin oponente. Los Bye legítimos
   * (oponente 0, reportados automáticamente con el jugador como ganador) no cuentan.
   */
  const incompleteTables = matches
    .filter((match) => {
      const isValidBye =
        !!match.playerId &&
        !match.opponentId &&
        !!match.reported &&
        match.winnerId === match.playerId
      return (!match.playerId || !match.opponentId) && !isValidBye
    })
    .map((match) => ({
      table: match.table,
      missing:
        !match.playerId && !match.opponentId
          ? "sin jugadores"
          : !match.playerId
            ? "sin jugador 1"
            : "sin jugador 2",
    }))

  useEffect(() => {
    setMobilePage(0)
  }, [showOnlyPending, tableSearch, matches.length])

  const handleTableSearchChange = (value: string) => {
    setTableSearch(value.replace(/[^0-9]/g, ""))
  }

  const reportedMatches = matches.filter((match) => match.reported).length
  const totalMatches = matches.length
  const progressPercentage = totalMatches > 0 ? (reportedMatches / totalMatches) * 100 : 0
  const canEditReportedMatch = (match: Match) => !match.reported || canModifyReportedResults
  const shouldShowModifyBlockedHint = (match: Match) =>
    canReport && !!match.reported && !canModifyReportedResults

  // Check if round is completed (all matches reported)
  const isRoundCompleted = reportedMatches === totalMatches && totalMatches > 0

  const getProgressBarColor = () => {
    if (progressPercentage === 100) return "bg-green-500 dark:bg-green-400"
    if (progressPercentage >= 75) return "bg-blue-500 dark:bg-blue-400"
    if (progressPercentage >= 50) return "bg-yellow-500 dark:bg-yellow-400"
    if (progressPercentage >= 25) return "bg-orange-500 dark:bg-orange-400"
    return "bg-red-500 dark:bg-red-400"
  }

  const openReleaseFlow = (matchId: number) => {
    setReleaseTargetMatchId(matchId)
    setShowReleaseConfirmDialog(true)
  }

  const openAssignFlow = (matchId: number) => {
    setAssignTargetMatchId(matchId)
    setSelectedPlayerOneId("")
    setSelectedPlayerTwoId("")
    setShowAssignDialog(true)
  }

  const handleConfirmReleaseTable = async () => {
    if (!releaseTargetMatchId || !onReleaseTable) return
    await onReleaseTable(releaseTargetMatchId)
    setShowReleaseConfirmDialog(false)
    setReleaseTargetMatchId(null)
  }

  const handleAssignPlayers = async () => {
    if (!assignTargetMatchId || !onAssignPlayersToTable) return

    const targetMatch = matches.find((match) => match.id === assignTargetMatchId)
    if (!targetMatch) return

    const targetHasPlayerOnly = !!targetMatch.playerId && !targetMatch.opponentId
    const targetIsEmpty = !targetMatch.playerId && !targetMatch.opponentId

    if (!selectedPlayerOneId) return
    if (selectedPlayerOneId === selectedPlayerTwoId) return

    const playerOne = playersWithoutTable.find((x) => String(x.playerId) === selectedPlayerOneId)

    const playerTwo = selectedPlayerTwoId
      ? playersWithoutTable.find((x) => String(x.playerId) === selectedPlayerTwoId)
      : null

    if (!playerOne) return
    if (!targetHasPlayerOnly && !targetIsEmpty) return

    await onAssignPlayersToTable({
      targetMatchId: assignTargetMatchId,
      playerOneSourceMatchId: playerOne.sourceMatchId,
      playerOneId: playerOne.playerId,
      playerTwoSourceMatchId: playerTwo?.sourceMatchId,
      playerTwoId: playerTwo?.playerId,
    })

    setShowAssignDialog(false)
    setAssignTargetMatchId(null)
    setSelectedPlayerOneId("")
    setSelectedPlayerTwoId("")
  }

  return (
    <div className="grid gap-2">
      {/* Buscador de mesas */}
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Buscar por número de mesa..."
          value={tableSearch}
          onChange={(e) => handleTableSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Controls Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        {((onShowOnlyPendingChange && !isRoundCompleted) ||
          (reportedMatches === totalMatches && !isRoundCompleted)) && (
          <div className="flex flex-col space-y-2 flex-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-pending"
                checked={showOnlyPending}
                onCheckedChange={onShowOnlyPendingChange}
              />
              <label
                htmlFor="show-pending"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-gray-200"
              >
                Mostrar solo partidas pendientes por reportar
              </label>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-muted dark:bg-muted-dark dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground dark:text-gray-400">
              {reportedMatches} de {totalMatches} partidas reportadas (
              {Math.round(progressPercentage)}%)
            </div>
          </div>
        )}

        {onPrintMatches && canReport && (
          <Button variant="outline" size="sm" onClick={onPrintMatches} className="shrink-0">
            <Printer className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Imprimir</span>
          </Button>
        )}
      </div>

      {/* Matches List */}
      {filteredMatches.map((match, index) => {
        const isInMobilePage = index >= mobilePageStart && index < mobilePageEnd
        return (
          <Card
            key={match.id}
            className={cn("overflow-hidden", !isInMobilePage && "hidden sm:block")}
          >
            {/* Mobile Layout */}
            <div className="block sm:hidden">
              <CardHeader className="py-3 bg-primary/80 dark:bg-gray-700">
                <CardTitle className="flex items-center justify-between text-base text-white dark:text-gray-100">
                  <div className="flex items-center gap-2">
                    <Table2 className="h-4 w-4 text-white dark:text-gray-300" />
                    <span className="font-bold">Mesa {match.table}</span>
                  </div>
                  {getMatchStatusBadge(match, true)}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {/* Player 1 - Mobile */}
                <div className="flex items-center justify-between mb-3 p-2 bg-muted/50 dark:bg-muted-dark/50 rounded-lg">
                  <div className="flex items-center gap-2 flex-1">
                    <UserAvatar
                      src={match?.Player?.Person?.urlAvatar}
                      name={match?.Player?.Person?.fullName}
                      size="xl"
                      className="hidden ring-1 ring-gray-200 dark:ring-gray-700" // opcional: agrega un borde adaptativo al avatar
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm break-words text-gray-900 dark:text-gray-100">
                        {match?.Player?.Person?.fullName} ({match?.Player?.points} puntos)
                      </p>
                      <p className="text-xs text-muted-foreground dark:text-gray-400">
                        {canSeeCode && match?.Player?.Person?.code}
                        {getRaceName(match?.Player?.raceId, isAdmin, true)}
                      </p>
                      {match?.Player?.drop === 1 && (
                        <Badge variant="destructive" className="text-xs dark:text-white-400">
                          Drop
                        </Badge>
                      )}
                    </div>

                    {/* Score */}
                    <div className="sm:ml-4 text-sm font-semibold text-center sm:text-right text-gray-900 dark:text-gray-100">
                      {getScoreDisplay(match, 1)}
                    </div>
                  </div>
                </div>

                {/* VS Section - Mobile */}
                <div className="hidden sm:block text-center mb-3">
                  {isByeMatch(match) ? (
                    <div className="text-center">
                      {match.reported && (
                        <div className="mt-2 flex flex-col items-center">
                          <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 mb-1" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {getScoreDisplay(match)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {Number(match.reported) === 1 && (
                        <div className="mt-2 text-sm text-gray-900 dark:text-gray-100">
                          {getScoreDisplay(match)}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Player 2 or BYE - Mobile */}
                {!isByeMatch(match) ? (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 p-2 bg-muted/50 dark:bg-muted-dark/50 rounded-lg gap-2">
                    {/* Avatar + Info */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <UserAvatar
                        src={match.Opponent?.Person?.urlAvatar}
                        name={match.Opponent?.Person?.fullName}
                        size="xl"
                        className="hidden"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate text-gray-900 dark:text-gray-100">
                          {match.Opponent?.Person?.fullName} ({match?.Opponent?.points} puntos)
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          {canSeeCode && match.Opponent?.Person?.code}
                          {getRaceName(match.Opponent?.raceId, isAdmin, true)}
                        </p>
                        {match.Opponent?.drop === 1 && (
                          <Badge variant="destructive" className="text-xs mt-1 dark:text-white-400">
                            Drop
                          </Badge>
                        )}
                      </div>
                      {/* Score */}
                      <div className="sm:ml-4 text-sm font-semibold text-center sm:text-right text-gray-900 dark:text-gray-100">
                        {getScoreDisplay(match, 2)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-1 justify-end">
                    <div className="text-right">
                      <p className="font-medium text-muted-foreground dark:text-gray-300">
                        Sin oponente
                      </p>
                      <p className="text-sm text-muted-foreground dark:text-gray-400">BYE</p>
                    </div>
                    <Avatar className="hidden">
                      <AvatarFallback className="dark:text-gray-100">
                        {getPlayerInitials("??")}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                )}

                {/* Action Button - Mobile */}
                {canReport && (
                  <div className="text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReportResult(match.id)}
                      disabled={!canReport || !canEditReportedMatch(match)}
                      className="w-full dark:text-gray-100 border-gray-300 dark:border-gray-600 dark:hover:bg-gray-300/80"
                    >
                      <Trophy className="h-4 w-4 mr-2  dark:text-gray-100" />
                      {match.reported ? "Modificar" : "Reportar"}
                    </Button>
                    {shouldShowModifyBlockedHint(match) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Solo organizadores pueden modificar resultados reportados.
                      </p>
                    )}
                  </div>
                )}
                {canManageTables && (
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    {canReleaseTables && onReleaseTable && (match.playerId || match.opponentId) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReleaseFlow(match.id)}
                        disabled={isUpdatingTables}
                        className="w-full"
                      >
                        <Unplug className="h-4 w-4 mr-2" />
                        Liberar mesa
                      </Button>
                    )}

                    {onAssignPlayersToTable &&
                      !match.opponentId &&
                      ((match.playerId && playersWithoutTable.length >= 1) ||
                        (!match.playerId && playersWithoutTable.length >= 1)) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openAssignFlow(match.id)}
                          disabled={isUpdatingTables}
                          className="w-full"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Asignar jugadores sin mesa
                        </Button>
                      )}
                  </div>
                )}
                {match.ModifiedBy && (
                  <div className="mt-2 text-xs text-muted-foreground dark:text-gray-400 text-center">
                    Reportado por: {match.ModifiedBy?.fullName} <br />
                    <span className="text-xs text-muted-foreground">
                      {new Date(match.modified).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                  </div>
                )}
              </CardContent>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:block">
              <CardContent
                className={cn(
                  "py-2 px-2 bg-white dark:bg-gray-800 grid gap-6",
                  isAdmin ? "grid-cols-[2fr_10fr]" : "grid-cols-[2fr_10fr]",
                )}
              >
                <div className="flex items-center justify-center flex-col">
                  <span className="flex items-center text-md font-semibold">
                    <Table2 className="h-4 w-4 text-gray-700 dark:text-gray-300 inline mr-2" />#{" "}
                    {match.table}
                  </span>
                  {match.reported === 1 && <span>{getMatchStatusBadge(match)}</span>}
                  {canManageTables && (
                    <div className="mt-2 flex flex-col gap-2 w-full">
                      {canReleaseTables &&
                        onReleaseTable &&
                        (match.playerId || match.opponentId) > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReleaseFlow(match.id)}
                            disabled={isUpdatingTables}
                            className="w-full"
                          >
                            <Unplug className="h-4 w-4 mr-2" />
                            Liberar mesa
                          </Button>
                        )}

                      {onAssignPlayersToTable &&
                        !match.opponentId &&
                        ((match.playerId && playersWithoutTable.length >= 1) ||
                          (!match.playerId && playersWithoutTable.length >= 1)) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openAssignFlow(match.id)}
                            disabled={isUpdatingTables}
                            className="w-full"
                          >
                            <Link2 className="h-4 w-4 mr-2" />
                            Asignar sin mesa
                          </Button>
                        )}
                    </div>
                  )}
                  {match.ModifiedBy && isAdmin && (
                    <div className="mt-2 text-xs text-muted-foreground dark:text-gray-400 text-center">
                      {match.ModifiedBy?.fullName} <br />
                      <span className="text-xs text-muted-foreground">
                        {new Date(match.modified).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between text-gray-900 dark:text-gray-100">
                  {/* Player 1 - Desktop */}
                  <div className="flex items-center gap-3 flex-1">
                    <UserAvatar
                      src={match?.Player?.Person?.urlAvatar}
                      name={match?.Player?.Person?.fullName}
                      size="xl"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {match?.Player?.Person?.fullName}
                      </p>
                      {canSeeCode && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Código: {match?.Player?.Person?.code}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Puntos: {match?.Player?.points}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {getRaceName(match?.Player?.raceId, isAdmin)}
                      </p>
                      {match?.Player?.drop === 1 && (
                        <Badge variant="destructive" className="text-xs dark:text-red-400">
                          Drop
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* VS and Result - Desktop */}
                  <div className="flex flex-col items-center gap-2 px-4 min-w-[120px]">
                    {isByeMatch(match) ? (
                      <div className="text-center">
                        {match.reported > 0 && (
                          <div className="text-center">
                            <div className="text-gray-900 dark:text-gray-100">
                              {getScoreDisplay(match)}
                            </div>
                            {canReport && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onReportResult(match.id)}
                                  disabled={!canReport || !canEditReportedMatch(match)}
                                  className="dark:border-gray-600 dark:text-gray-100 mt-2"
                                >
                                  <Trophy className="h-4 w-4 mr-1" />
                                  {match.reported ? "Modificar" : "Reportar"}
                                </Button>
                                {shouldShowModifyBlockedHint(match) && (
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    Solo organizadores pueden modificar resultados reportados.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {match.reported ? (
                          <div className="text-center">
                            <div className="text-gray-900 dark:text-gray-100">
                              {getScoreDisplay(match)}
                            </div>
                            {canReport && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onReportResult(match.id)}
                                  disabled={!canReport || !canEditReportedMatch(match)}
                                  className="mt-2 dark:border-gray-600 dark:text-gray-100"
                                >
                                  <Trophy className="h-4 w-4 mr-1" />
                                  {match.reported ? "Modificar" : "Reportar"}
                                </Button>
                                {shouldShowModifyBlockedHint(match) && (
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    Solo organizadores pueden modificar resultados reportados.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        ) : canReport ? (
                          <div className="text-center">
                            <div className="text-gray-900 dark:text-gray-100">
                              {getScoreDisplay(match)}
                            </div>
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onReportResult(match.id)}
                                disabled={!canReport || !canEditReportedMatch(match)}
                                className="dark:border-gray-600 dark:text-gray-100 mt-2"
                              >
                                <Trophy className="h-4 w-4 mr-1" />
                                {match.reported ? "Modificar" : "Reportar"}
                              </Button>
                              {shouldShowModifyBlockedHint(match) && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Solo organizadores pueden modificar resultados reportados.
                                </p>
                              )}
                            </>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="text-gray-900 dark:text-gray-100">
                              {getScoreDisplay(match)}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Player 2 or BYE - Desktop */}
                  {isByeMatch(match) ? (
                    <div className="flex items-center gap-3 flex-1 justify-end">
                      <div className="text-right">
                        <p className="font-medium text-muted-foreground dark:text-gray-300">
                          Sin oponente
                        </p>
                        <p className="text-sm text-muted-foreground dark:text-gray-400">BYE</p>
                      </div>
                      <Avatar>
                        <AvatarFallback className="dark:text-gray-100">
                          {getPlayerInitials("??")}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-1 justify-end">
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {match.Opponent?.Person?.fullName}
                        </p>
                        {canSeeCode && (
                          <p className="text-sm text-muted-foreground dark:text-gray-400">
                            Código: {match.Opponent?.Person?.code}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground dark:text-gray-400">
                          Puntos: {match?.Opponent?.points}
                        </p>
                        <p className="text-sm text-muted-foreground dark:text-gray-400">
                          {getRaceName(match.Opponent?.raceId, isAdmin)}
                        </p>
                        {match.Opponent?.drop === 1 && (
                          <Badge variant="destructive" className="text-xs dark:text-red-400">
                            Drop
                          </Badge>
                        )}
                      </div>
                      <UserAvatar
                        src={match.Opponent?.Person?.urlAvatar}
                        name={match.Opponent?.Person?.fullName}
                        size="xl"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </div>
          </Card>
        )
      })}

      {/* Mobile pagination */}
      {totalMobilePages > 1 && (
        <div className="flex sm:hidden items-center justify-between gap-2 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMobilePage((page) => Math.max(0, page - 1))}
            disabled={mobilePage === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="text-xs text-muted-foreground dark:text-gray-400">
            Página {mobilePage + 1} de {totalMobilePages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMobilePage((page) => Math.min(totalMobilePages - 1, page + 1))}
            disabled={mobilePage >= totalMobilePages - 1}
          >
            Siguiente
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {(isAdmin || canManageTables) && incompleteTables.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <div className="mb-2 flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4" />
            Mesas incompletas
          </div>
          <ul className="list-disc pl-5 text-sm">
            {incompleteTables.map((item) => (
              <li key={item.table}>
                Mesa #{item.table} — {item.missing}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(isAdmin || canManageTables) && duplicatedPlayers.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
          <div className="mb-2 flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4" />
            Jugadores repetidos en los emparejamientos
          </div>
          <ul className="list-disc pl-5 text-sm">
            {duplicatedPlayers.map((player) => (
              <li key={player.playerId}>
                {player.name}
                {player.code ? ` (${player.code})` : ""} — mesas:{" "}
                {player.tables.map((table) => `#${table}`).join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <AlertDialog open={showReleaseConfirmDialog} onOpenChange={setShowReleaseConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Liberar esta mesa?</AlertDialogTitle>
            <AlertDialogDescription>
              Se quitarán los jugadores de esta mesa y quedará disponible para reasignación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmReleaseTable} disabled={isUpdatingTables}>
              Sí, liberar mesa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar jugadores sin mesa</DialogTitle>
            <DialogDescription>
              {(() => {
                const target = matches.find((match) => match.id === assignTargetMatchId)
                if (target?.playerId && !target?.opponentId) {
                  return "Selecciona un jugador sin mesa para completar esta mesa."
                }
                return "Selecciona al menos un jugador sin mesa para asociarlo. El segundo jugador es opcional."
              })()}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Jugador 1</label>
              <Select value={selectedPlayerOneId} onValueChange={setSelectedPlayerOneId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar jugador" />
                </SelectTrigger>
                <SelectContent>
                  {playersWithoutTable.map((player) => (
                    <SelectItem key={player.playerId} value={String(player.playerId)}>
                      {player.name} {player.code ? `(${player.code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(() => {
              const target = matches.find((match) => match.id === assignTargetMatchId)
              const showOptionalSecondPlayer = !target?.playerId && !target?.opponentId

              if (!showOptionalSecondPlayer) {
                return null
              }

              return (
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Jugador 2</label>
                  <Select value={selectedPlayerTwoId} onValueChange={setSelectedPlayerTwoId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar jugador" />
                    </SelectTrigger>
                    <SelectContent>
                      {playersWithoutTable
                        .filter((player) => String(player.playerId) !== selectedPlayerOneId)
                        .map((player) => (
                          <SelectItem key={player.playerId} value={String(player.playerId)}>
                            {player.name} {player.code ? `(${player.code})` : ""}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAssignPlayers}
              disabled={
                isUpdatingTables ||
                !selectedPlayerOneId ||
                selectedPlayerOneId === selectedPlayerTwoId
              }
            >
              Asociar jugadores
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
